import { FC, useMemo, useState } from 'react';
import { Input, Select } from '../components/Form';
import { SortableHeader } from '../components/SortableHeader';
import { useSorting } from '../hooks/useSorting';
import {
  useGetTariffUsageQuery,
} from '../api/api';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const formatMonthYear = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  const day = date.getUTCDate();
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const year = date.getUTCFullYear();
  return `${day} ${month.toLowerCase()} ${year}`;
};

const getPurposeTypeChipClasses = (purposeType: string): string => {
  if (purposeType === 'paid') return 'bg-green-100 text-green-800';
  if (purposeType === 'trial') return 'bg-blue-100 text-blue-800';
  if (purposeType === 'internal') return 'bg-gray-100 text-gray-800';
  return 'bg-purple-100 text-purple-800';
};

const getPeriodProgressPercent = (periodStartSeconds: number, periodEndSeconds: number): number => {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const totalSeconds = Math.max(periodEndSeconds - periodStartSeconds, 1);
  const elapsedSeconds = Math.min(Math.max(nowSeconds - periodStartSeconds, 0), totalSeconds);
  return (elapsedSeconds / totalSeconds) * 100;
};

const getTariffUtilizationRatio = (
  periodStartSeconds: number,
  periodEndSeconds: number,
  sessionLimit: number,
  sessionsUsed: number,
): number | null => {
  if (sessionLimit <= 0) return null;

  const nowMs = Date.now();
  const periodStartMs = periodStartSeconds * 1000;
  const periodEndMs = periodEndSeconds * 1000;

  const totalSubscriptionDays = Math.max((periodEndMs - periodStartMs) / MS_PER_DAY, 0);
  if (totalSubscriptionDays <= 0) return null;

  const elapsedDaysRaw = (nowMs - periodStartMs) / MS_PER_DAY;
  const elapsedDays = Math.min(Math.max(elapsedDaysRaw, 0), totalSubscriptionDays);
  const elapsedRate = elapsedDays / totalSubscriptionDays;
  if (elapsedRate <= 0) return null;

  const usageRate = sessionsUsed / sessionLimit;
  return usageRate / elapsedRate;
};

interface TariffUsageRow {
  id: string;
  organization_name: string;
  purpose_type: string;
  period_start: number;
  period_end: number;
  period_start_ts: number;
  period_end_ts: number;
  period_progress_percent: number;
  sessions_used: number;
  session_limit: number;
  session_usage_percent: number | null;
  session_usage_sort_value: number;
  tariff_utilization_ratio: number | null;
  tariff_utilization_sort_value: number;
}

export const TariffUsage: FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedPurposeType, setSelectedPurposeType] = useState<string>('paid');
  const [selectedActive, setSelectedActive] = useState<string>('Active');
  const { data: tariffUsageRows, isLoading: isLoadingTariffUsage } = useGetTariffUsageQuery();

  const filteredPeriods = useMemo(() => {
    if (!tariffUsageRows) return [];

    const nowTimestamp = Math.floor(Date.now() / 1000);
    const loweredSearchTerm = searchTerm.trim().toLowerCase();

    return tariffUsageRows.filter((period) => {
      const matchesSearch = loweredSearchTerm.length === 0
        || period.organization_name.toLowerCase().includes(loweredSearchTerm);

      const matchesPurposeType = selectedPurposeType === 'All Types'
        || selectedPurposeType === period.purpose_type;

      const periodStart = period.period_start;
      const periodEnd = period.period_end;
      const isActive = nowTimestamp >= periodStart && nowTimestamp <= periodEnd;

      const matchesActive = selectedActive === 'All Periods'
        || (selectedActive === 'Active' && isActive)
        || (selectedActive === 'Inactive' && !isActive);

      return matchesSearch && matchesPurposeType && matchesActive;
    });
  }, [tariffUsageRows, searchTerm, selectedPurposeType, selectedActive]);

  const usageRows = useMemo<TariffUsageRow[]>(() => {
    return filteredPeriods.map((period) => {
      const periodStartTs = period.period_start;
      const periodEndTs = period.period_end;
      const sessionsUsed = period.sessions_used ?? 0;
      const sessionLimit = period.session_limit ?? 0;
      const usagePercent = sessionLimit > 0 ? (sessionsUsed / sessionLimit) * 100 : null;
      const utilizationRatio = getTariffUtilizationRatio(
        periodStartTs,
        periodEndTs,
        sessionLimit,
        sessionsUsed,
      );

      return {
        id: period.id,
        organization_name: period.organization_name,
        purpose_type: period.purpose_type,
        period_start: periodStartTs,
        period_end: periodEndTs,
        period_start_ts: periodStartTs,
        period_end_ts: periodEndTs,
        period_progress_percent: getPeriodProgressPercent(periodStartTs, periodEndTs),
        sessions_used: sessionsUsed,
        session_limit: sessionLimit,
        session_usage_percent: usagePercent,
        session_usage_sort_value: usagePercent ?? -1,
        tariff_utilization_ratio: utilizationRatio,
        tariff_utilization_sort_value: utilizationRatio ?? -1,
      };
    });
  }, [filteredPeriods]);

  const { items: sortedRows, requestSort, sortConfig } = useSorting<TariffUsageRow>(usageRows, {
    key: 'period_start_ts',
    direction: 'desc',
  });

  if (isLoadingTariffUsage) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Tariff Usage</h1>
      </div>

      <div className="mb-6">
        <div className="flex space-x-4">
          <div className="flex-1">
            <Input
              label="Organization Search"
              type="text"
              placeholder="Search by organization name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="w-64">
            <Select
              label="Purpose Type"
              value={selectedPurposeType}
              onChange={(e) => setSelectedPurposeType(e.target.value)}
              options={[
                { _id: 'All Types', name: 'All Types' },
                { _id: 'paid', name: 'Paid' },
                { _id: 'partner', name: 'Partner' },
                { _id: 'internal', name: 'Internal' },
                { _id: 'trial', name: 'Trial' },
              ]}
              showEmptyOption={false}
            />
          </div>
          <div className="w-64">
            <Select
              label="Period"
              value={selectedActive}
              onChange={(e) => setSelectedActive(e.target.value)}
              options={[
                { _id: 'All Periods', name: 'All Periods' },
                { _id: 'Active', name: 'Active' },
                { _id: 'Inactive', name: 'Inactive' },
              ]}
              showEmptyOption={false}
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader<TariffUsageRow>
                label="Organization Name"
                sortKey="organization_name"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<TariffUsageRow>
                label="Period"
                sortKey="period_start_ts"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<TariffUsageRow>
                label="Purpose Type"
                sortKey="purpose_type"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<TariffUsageRow>
                label="Session Usage (including manual deductions)"
                sortKey="session_usage_sort_value"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<TariffUsageRow>
                label="Tariff Utilization Ratio"
                sortKey="tariff_utilization_sort_value"
                currentSort={sortConfig}
                onSort={requestSort}
              />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedRows.map((row) => {
              return (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{row.organization_name}</td>
                  <td className="px-6 py-4">
                    <div className="min-w-64">
                      <div className="text-sm text-gray-700">
                        {formatMonthYear(row.period_start)} - {formatMonthYear(row.period_end)}
                      </div>
                      <div className="mt-2 h-2 w-full border border-gray-300 rounded-full overflow-hidden bg-white">
                        <div
                          className="h-full bg-blue-600"
                          style={{ width: `${row.period_progress_percent}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPurposeTypeChipClasses(row.purpose_type)}`}>
                      {row.purpose_type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="min-w-64">
                        <div className="text-sm text-gray-700">
                        {`${row.sessions_used} / ${row.session_limit < 0 ? 'Unlimited' : row.session_limit}`}
                      </div>
                      <div className="mt-2 h-2 w-full border border-gray-300 rounded-full overflow-hidden bg-white">
                        <div
                          className="h-full bg-blue-600"
                          style={{ width: `${Math.min(Math.max(row.session_usage_percent ?? 0, 0), 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.tariff_utilization_ratio === null ? 'N/A' : row.tariff_utilization_ratio.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
